import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import { ROLES } from 'config/roleConfig';
import { ROUTES } from 'config/navConfig';

// Cyan glass tokens — isolated to the floating layout only
const C = {
  cardBg:       'rgba(4, 14, 24, 0.88)',
  cardBorder:   'rgba(6, 182, 212, 0.20)',
  cardShadow:   '0 0 0 1px rgba(6, 182, 212, 0.11), 0 10px 32px rgba(0, 0, 0, 0.58)',
  cardHoverBg:  'rgba(6, 182, 212, 0.07)',
  cardHoverBdr: 'rgba(6, 182, 212, 0.34)',
  iconGradient: 'linear-gradient(135deg, #22d3ee 0%, #0891b2 100%)',
  iconGlow:     '0 0 10px rgba(6, 182, 212, 0.45)',
  textPrimary:  'rgba(255, 255, 255, 0.90)',
  textMuted:    'rgba(255, 255, 255, 0.44)',
};

const ACTIONS_BY_CONTEXT = {
  'portfolio-dashboard': [
    {
      label: 'Browse Opportunities',
      description: 'Explore new infrastructure deals',
      icon: 'Search',
      path: ROUTES.opportunities,
      variant: 'default',
      permission: [
        ROLES.INVESTMENT_TEAM, ROLES.IC_MEMBER, ROLES.ASSET_MGMT_TEAM,
        ROLES.FINANCE_TEAM, ROLES.PLATFORM_ADMIN,
      ],
    },
    {
      label: 'Create SPV',
      description: 'Structure a new investment vehicle',
      icon: 'Plus',
      path: ROUTES.spvCreation,
      variant: 'outline',
      permission: [ROLES.INVESTMENT_TEAM, ROLES.PLATFORM_ADMIN],
    },
    {
      label: 'View Pipeline',
      description: 'Track deal progression',
      icon: 'GitBranch',
      path: ROUTES.pipeline,
      variant: 'outline',
      permission: [ROLES.INVESTMENT_TEAM, ROLES.PLATFORM_ADMIN],
    },
  ],
  'opportunities-list': [
    {
      label: 'Create SPV',
      description: 'Structure a new investment vehicle',
      icon: 'Plus',
      path: ROUTES.spvCreation,
      variant: 'default',
      permission: [ROLES.INVESTMENT_TEAM, ROLES.PLATFORM_ADMIN],
    },
  ],
  'spv-dashboard': [
    {
      label: 'New SPV',
      description: 'Create another investment vehicle',
      icon: 'Plus',
      path: ROUTES.spvCreation,
      variant: 'default',
      permission: [ROLES.INVESTMENT_TEAM, ROLES.PLATFORM_ADMIN],
    },
    {
      label: 'Browse Opportunities',
      description: 'Find deals for your SPV',
      icon: 'Search',
      path: ROUTES.opportunities,
      variant: 'outline',
      permission: [
        ROLES.INVESTMENT_TEAM, ROLES.IC_MEMBER, ROLES.PLATFORM_ADMIN,
        ROLES.CO_INVESTOR,
      ],
    },
  ],
  'pipeline': [
    {
      label: 'Submit to IC',
      description: 'Create an IC submission for a deal',
      icon: 'ClipboardCheck',
      path: ROUTES.icSubmission,
      variant: 'default',
      permission: [ROLES.INVESTMENT_TEAM, ROLES.PLATFORM_ADMIN],
    },
    {
      label: 'IC Submissions',
      description: 'Review pending submissions',
      icon: 'ClipboardList',
      path: ROUTES.icSubmission,
      variant: 'outline',
      permission: [ROLES.IC_MEMBER],
    },
  ],
  'ic-submission': [
    {
      label: 'View Pipeline',
      description: 'Return to deal pipeline',
      icon: 'GitBranch',
      path: ROUTES.pipeline,
      variant: 'outline',
      permission: [ROLES.INVESTMENT_TEAM, ROLES.PLATFORM_ADMIN],
    },
    {
      label: 'Review Queue',
      description: 'Submissions pending your review',
      icon: 'ClipboardList',
      path: ROUTES.icSubmission,
      variant: 'default',
      permission: [ROLES.IC_MEMBER, ROLES.PLATFORM_ADMIN],
    },
  ],
};

const DEFAULT_ACTIONS = [
  {
    label: 'Dashboard',
    description: 'Return to portfolio overview',
    icon: 'LayoutDashboard',
    path: ROUTES.dashboard,
    variant: 'outline',
    permission: [
      ROLES.INVESTMENT_TEAM, ROLES.IC_MEMBER, ROLES.ASSET_MGMT_TEAM,
      ROLES.FINANCE_TEAM, ROLES.LEGAL_COMP_TEAM, ROLES.PLATFORM_ADMIN,
    ],
  },
];

export default function QuickActionsPanel({
  context = 'portfolio-dashboard',
  userRole = 'investor',
  layout = 'horizontal',
  className = '',
}) {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);

  const contextActions = ACTIONS_BY_CONTEXT?.[context] || DEFAULT_ACTIONS;
  const filteredActions = contextActions?.filter((action) =>
    action?.permission?.includes(userRole)
  );

  if (filteredActions?.length === 0) return null;

  const handleAction = (path) => {
    navigate(path);
    setIsExpanded(false);
  };

  // Floating layout
  if (layout === 'floating') {
    return (
      <>
        <div className="fixed bottom-6 right-6 flex flex-col items-end gap-3" style={{ zIndex: 'var(--z-dropdown)' }}>

          {/* Action cards — glassmorphic cyan */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                className="flex flex-col gap-2 items-end"
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={{
                  visible: { transition: { staggerChildren: 0.07 } },
                  hidden:  { transition: { staggerChildren: 0.04, staggerDirection: -1 } },
                }}
              >
                {filteredActions?.map((action, index) => (
                  <motion.button
                    key={index}
                    onClick={() => handleAction(action?.path)}
                    variants={{
                      hidden:  { opacity: 0, x: 16, scale: 0.94 },
                      visible: { opacity: 1, x: 0, scale: 1, transition: { duration: 0.22, ease: [0.175, 0.885, 0.32, 1.05] } },
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 14px',
                      background: C.cardBg,
                      backdropFilter: 'blur(24px) saturate(160%)',
                      WebkitBackdropFilter: 'blur(24px) saturate(160%)',
                      border: `1px solid ${C.cardBorder}`,
                      borderRadius: '14px',
                      boxShadow: C.cardShadow,
                      cursor: 'pointer',
                      transition: 'background 0.15s, border-color 0.15s',
                      whiteSpace: 'nowrap',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = C.cardHoverBg;
                      e.currentTarget.style.borderColor = C.cardHoverBdr;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = C.cardBg;
                      e.currentTarget.style.borderColor = C.cardBorder;
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flex: 1, minWidth: 0 }}>
                      <span
                        className="font-caption"
                        style={{ fontSize: '0.8125rem', fontWeight: 600, color: C.textPrimary }}
                      >
                        {action?.label}
                      </span>
                      {action?.description && (
                        <span
                          className="font-caption"
                          style={{ fontSize: '0.72rem', color: C.textMuted, marginTop: '1px' }}
                        >
                          {action?.description}
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '32px',
                        height: '32px',
                        borderRadius: '9px',
                        background: C.iconGradient,
                        boxShadow: C.iconGlow,
                        flexShrink: 0,
                      }}
                    >
                      <Icon name={action?.icon} size={15} color="white" />
                    </div>
                  </motion.button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* FAB */}
          <button
            onClick={() => setIsExpanded((prev) => !prev)}
            className={`flex items-center justify-center w-14 h-14 rounded-full transition-all duration-300 active:scale-95 ${!isExpanded ? 'qap-fab-glow-pulse' : ''}`}
            style={{
              background: 'linear-gradient(135deg, #22d3ee 0%, #0891b2 100%)',
              boxShadow: isExpanded
                ? '0 0 0 2px rgba(6, 182, 212, 0.28), 0 6px 24px rgba(0,0,0,0.40)'
                : undefined,
              border: 'none',
              cursor: 'pointer',
            }}
            aria-label="Quick actions"
            aria-expanded={isExpanded}
          >
            <Icon name={isExpanded ? 'X' : 'Zap'} size={22} color="white" strokeWidth={2} />
          </button>
        </div>

        {isExpanded && (
          <div
            className="fixed inset-0"
            style={{ zIndex: 'calc(var(--z-dropdown) - 1)' }}
            onClick={() => setIsExpanded(false)}
            aria-hidden="true"
          />
        )}
      </>
    );
  }

  // Vertical layout
  if (layout === 'vertical') {
    return (
      <div className={`flex flex-col gap-2 ${className}`}>
        <p
          className="font-caption text-muted-foreground mb-1"
          style={{ fontSize: '0.75rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}
        >
          Quick Actions
        </p>
        {filteredActions?.map((action, index) => (
          <button
            key={index}
            onClick={() => handleAction(action?.path)}
            className="flex items-center gap-3 p-3 bg-card border border-border rounded-md hover:border-primary hover:shadow-sm transition-all duration-250 text-left group min-h-touch"
          >
            <div
              className="flex items-center justify-center w-9 h-9 rounded-md flex-shrink-0 transition-all duration-250 group-hover:scale-105"
              style={{ background: 'rgba(59, 76, 76, 0.08)' }}
            >
              <Icon name={action?.icon} size={18} color="var(--color-primary)" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-caption font-medium text-foreground text-sm">{action?.label}</p>
              <p className="font-caption text-muted-foreground truncate" style={{ fontSize: '0.75rem' }}>
                {action?.description}
              </p>
            </div>
            <Icon name="ChevronRight" size={16} color="var(--color-muted-foreground)" className="flex-shrink-0" />
          </button>
        ))}
      </div>
    );
  }

  // Horizontal layout (default)
  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      {filteredActions?.map((action, index) => (
        <Button
          key={index}
          variant={action?.variant === 'default' ? 'default' : 'outline'}
          iconName={action?.icon}
          iconPosition="left"
          iconSize={16}
          onClick={() => handleAction(action?.path)}
          className="hover-lift"
        >
          {action?.label}
        </Button>
      ))}
    </div>
  );
}
